import { useEffect, useState } from 'react'
import {
  Download, Search, Filter, TrendingUp,
  Calendar, CheckCircle, XCircle, Clock
} from 'lucide-react'
import ReportGenerator from '../../components/admin/ReportGenerator'

interface Payment {
  _id: string
  razorpayPaymentId: string
  razorpayOrderId?: string
  studentName: string
  studentEmail: string
  courseOrSession: string
  type: 'course' | 'live_session'
  amount: number
  status: 'completed' | 'failed' | 'pending'
  paymentMethod?: string
  createdAt: string
}

export default function AdminPayments() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    fetchPayments()
  }, [])

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/payments/admin', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPayments(data.payments || [])
      }
    } catch (error) {
      console.error('Error fetching payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const exportCSV = () => {
    const headers = [
      'Transaction ID',
      'Student',
      'Email',
      'Course/Session',
      'Type',
      'Amount',
      'Status',
      'Date'
    ]

    const csvData = filteredPayments.map(p => [
      p.razorpayPaymentId,
      p.studentName,
      p.studentEmail,
      p.courseOrSession,
      p.type,
      p.amount,
      p.status,
      new Date(p.createdAt).toLocaleDateString()
    ])

    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.studentEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.razorpayPaymentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.courseOrSession.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus
    const matchesType = filterType === 'all' || payment.type === filterType

    const paymentDate = new Date(payment.createdAt)
    const matchesDateFrom = !dateFrom || paymentDate >= new Date(dateFrom)
    const matchesDateTo = !dateTo || paymentDate <= new Date(dateTo)

    return matchesSearch && matchesStatus && matchesType && matchesDateFrom && matchesDateTo
  })

  const stats = {
    totalRevenue: payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
    totalTransactions: payments.length,
    successfulPayments: payments.filter(p => p.status === 'completed').length,
    failedPayments: payments.filter(p => p.status === 'failed').length,
    pendingPayments: payments.filter(p => p.status === 'pending').length
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: {
        color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
        icon: CheckCircle
      },
      failed: {
        color: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
        icon: XCircle
      },
      pending: {
        color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
        icon: Clock
      }
    }
    return badges[status as keyof typeof badges] || badges.pending
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-rose-50 dark:bg-rose-900/20 rounded-2xl">
              <TrendingUp className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">
                ₹{stats.totalRevenue.toLocaleString()}
              </p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Total Revenue</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900 dark:text-white">
                {stats.totalTransactions}
              </p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Transactions</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.successfulPayments}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Successful</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failedPayments}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Failed</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.pendingPayments}</p>
              <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Pending</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Generator */}
      <ReportGenerator />

      {/* Toolbar & Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-2.5 border rounded-xl transition-all ${showFilters
                ? 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-400'
                : 'border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300'
                }`}
            >
              <Filter className="h-5 w-5" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportCSV}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>

        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-dark-700 animate-slide-down">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Status
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Type
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                >
                  <option value="all">All Types</option>
                  <option value="course">Courses</option>
                  <option value="live_session">Live Sessions</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-700 border-b border-gray-100 dark:border-dark-600">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Transaction ID</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Student</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Course/Session</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-charcoal-900 dark:text-white uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredPayments.map((payment) => {
                const statusBadge = getStatusBadge(payment.status)
                const StatusIcon = statusBadge.icon

                return (
                  <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-medium text-charcoal-600 dark:text-charcoal-300 bg-gray-100 dark:bg-dark-900/50 px-2 py-1 rounded-lg">
                        {payment.razorpayPaymentId}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-charcoal-900 dark:text-white">{payment.studentName}</p>
                        <p className="text-sm text-charcoal-500 dark:text-charcoal-400">{payment.studentEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-charcoal-900 dark:text-white font-medium">{payment.courseOrSession}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-lg ${payment.type === 'course'
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
                        }`}>
                        {payment.type === 'course' ? 'Course' : 'Live Session'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        ₹{payment.amount.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-lg flex items-center gap-1.5 w-fit ${statusBadge.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-charcoal-500 dark:text-charcoal-400">
                        {new Date(payment.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-charcoal-400" />
            </div>
            <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
              No transactions found
            </h3>
            <p className="text-charcoal-500 dark:text-charcoal-400">
              Try adjusting your filters or search query
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
